import { AbstractTransport } from './abstract-transport.model';
import dayjs from 'dayjs';
import { Estimation } from './estimation.model';
import { OfferMerchandiseTypeEnum } from './enums/offer-merchandise-type.enum';
import { OfferServiceTypeEnum } from './enums/offer-service-type.enum';
import { TransportItem } from './transport-item.model';
import { Civility } from './enums/civility.enum';
import { HandlingTypeEnum } from './enums/handling-type.enum';
import { QuotationStatusEnum } from './enums/quotation-status.enum';
import { DATE_FORMAT } from './constants/input.constants';
import { LIST_SERVICE_TYPE } from './offer-service-type.model';
import { LIST_MARCHANDISE_TYPE } from './offer-merchandise-type.model';
import { LoadingConstraintEnum } from './enums/loading-constraint.enum';
import { AccessConstraintEnum } from './enums/access-constraint.enum';

export class Transport extends AbstractTransport {
  public totalOtherUM?: number;
  public estimation?: Estimation;

  constructor(data: any = {}) {
    super();
    Object.assign(this, data);
    this.pickupDate = data.pickupDate ? dayjs(data.pickupDate) : undefined;
    this.deliveryDate = data.deliveryDate ? dayjs(data.deliveryDate) : undefined;
    this.deliveryMaxDate = data.deliveryMaxDate ? dayjs(data.deliveryMaxDate) : undefined;
    this.creationDate = data.creationDate ? dayjs(data.creationDate) : dayjs(Date.now());

    this.estimation = new Estimation(data.estimation ?? {});

    this.merchandiseType = LIST_MARCHANDISE_TYPE[data.merchandiseType || OfferMerchandiseTypeEnum.CONDITIONED];
    this.serviceType = LIST_SERVICE_TYPE[data.serviceType || OfferServiceTypeEnum.LTL];
    this.items = data.items ? (data.items || []).map((item: any) => new TransportItem(item)) : [];
    this.quotationStatus = data.quotationStatus || QuotationStatusEnum.NONE;

    if (data.pickupAddress) {
      const address = data.pickupAddress;
      this.pickupAddressStreetNumber = normalizeEmptyString(address.streetNumber);
      this.pickupAddressStreet = normalizeEmptyString(address.street) ?? '';
      this.pickupAddressCity = normalizeEmptyString(address.city);
      this.pickupAddressZipCode = normalizeEmptyString(address.zipCode);
      this.pickupAddressInseeCode = normalizeEmptyString(address.cityCode);
      this.pickupAddressFullname = normalizeEmptyString(address.fullname) ?? '';
      this.pickupAddressAdditionals = normalizeEmptyString(address.additionals);
      this.pickupAddressCountry = normalizeEmptyString(address.country);
      this.pickupAddressGpsCoords = normalizeEmptyString(address.gps_coords);
    }

    if (data.deliveryAddress) {
      const address = data.deliveryAddress;
      this.deliveryAddressStreetNumber = normalizeEmptyString(address.streetNumber);
      this.deliveryAddressStreet = normalizeEmptyString(address.street) ?? '';
      this.deliveryAddressCity = normalizeEmptyString(address.city);
      this.deliveryAddressZipCode = normalizeEmptyString(address.zipCode);
      this.deliveryAddressInseeCode = normalizeEmptyString(address.cityCode);
      this.deliveryAddressFullname = normalizeEmptyString(address.fullname) ?? '';
      this.deliveryAddressAdditionals = normalizeEmptyString(address.additionals);
      this.deliveryAddressCountry = normalizeEmptyString(address.country);
      this.deliveryAddressGpsCoords = normalizeEmptyString(address.gps_coords);
    }

    if (data.pickupContact) {
      const contact = data.pickupContact;
      this.pickupContactCivility = contact.civility ?? Civility.MALE;
      this.pickupContactFirstName = normalizeEmptyString(contact.firstName);
      this.pickupContactLastName = normalizeEmptyString(contact.lastName);
      this.pickupContactMail = normalizeEmptyString(contact.mail);
      this.pickupContactPhone = normalizeEmptyString(contact.phone);
    }

    if (data.deliveryContact) {
      const contact = data.deliveryContact;
      this.deliveryContactCivility = contact.civility ?? Civility.MALE;
      this.deliveryContactFirstName = normalizeEmptyString(contact.firstName);
      this.deliveryContactLastName = normalizeEmptyString(contact.lastName);
      this.deliveryContactMail = normalizeEmptyString(contact.mail);
      this.deliveryContactPhone = normalizeEmptyString(contact.phone);
    }

    this.pickupAccessConstraint = data.pickupAccessConstraint || AccessConstraintEnum.NONE;
    this.pickupLoadingConstraint = data.pickupLoadingConstraint || LoadingConstraintEnum.NONE;

    this.deliveryAccessConstraint = data.deliveryAccessConstraint || AccessConstraintEnum.NONE;
    this.deliveryLoadingConstraint = data.deliveryLoadingConstraint || LoadingConstraintEnum.NONE;

    this.pickupReference = normalizeEmptyString(data.pickupReference);
    this.deliveryReference = normalizeEmptyString(data.deliveryReference);
  }

  public serialize(): any {
    this.updateTotalsTransport();
    return {
      id: this.id,
      items: (this.items || []).map(item => item.serialize()),
      itemsCondition: this.itemsCondition,
      insuranceItem: this.insuranceItem,
      basket: this.basket,
      customerId: this.customerId,
      isHazardousMaterial: this.isHazardousMaterial,
      isControlledTemperature: this.isControlledTemperature,
      merchandiseType: this.merchandiseType.code,
      serviceType: this.serviceType?.code,
      pickupAddressStreetNumber: this.pickupAddressStreetNumber,
      pickupAddressStreet: this.pickupAddressStreet,
      pickupAddressCity: this.pickupAddressCity,
      pickupAddressZipCode: this.pickupAddressZipCode,
      pickupAddressInseeCode: this.pickupAddressInseeCode,
      pickupAddressFullname: this.pickupAddressFullname,
      pickupAddressAdditionals: this.pickupAddressAdditionals,
      pickupAddressCountry: this.pickupAddressCountry,
      pickupAddressGpsCoords: this.pickupAddressGpsCoords,
      deliveryAddressStreetNumber: this.deliveryAddressStreetNumber,
      deliveryAddressStreet: this.deliveryAddressStreet,
      deliveryAddressCity: this.deliveryAddressCity,
      deliveryAddressZipCode: this.deliveryAddressZipCode,
      deliveryAddressInseeCode: this.deliveryAddressInseeCode,
      deliveryAddressFullname: this.deliveryAddressFullname,
      deliveryAddressAdditionals: this.deliveryAddressAdditionals,
      deliveryAddressCountry: this.deliveryAddressCountry,
      deliveryAddressGpsCoords: this.deliveryAddressGpsCoords,
      deliveryContactCivility: this.deliveryContactCivility,
      deliveryContactFirstName: this.deliveryContactFirstName,
      deliveryContactLastName: this.deliveryContactLastName,
      deliveryContactMail: this.deliveryContactMail,
      deliveryContactPhone: this.deliveryContactPhone,
      deliveryAccessConstraint: this.serviceType.code === OfferServiceTypeEnum.FTL ? undefined : this.deliveryAccessConstraint, //Si le transport est conditionné, on supprime les contraintes d'accès
      deliveryLoadingConstraint: this.deliveryLoadingConstraint,
      deliveryRegisteredAddressId: this.deliveryRegisteredAddressId,
      deliveryTimeslotEnd: this.deliveryTimeslotEnd,
      deliveryTimeslotStart: this.deliveryTimeslotStart,
      deliveryDate: this.deliveryDate && dayjs(this.deliveryDate).isValid() ? dayjs(this.deliveryDate).format(DATE_FORMAT) : undefined,
      deliveryMaxDate: this.deliveryMaxDate && dayjs(this.deliveryMaxDate).isValid() ? dayjs(this.deliveryMaxDate).format(DATE_FORMAT) : undefined,
      deliveryComments: this.deliveryComments,
      deliveryFloor: this.deliveryFloor,
      deliveryMeeting: this.deliveryMeeting,
      deliveryPhoneCall: this.deliveryPhoneCall,
      deliveryReference: this.deliveryReference,
      pickupContactCivility: this.pickupContactCivility,
      pickupContactFirstName: this.pickupContactFirstName,
      pickupContactLastName: this.pickupContactLastName,
      pickupContactMail: this.pickupContactMail,
      pickupContactPhone: this.pickupContactPhone,
      pickupAccessConstraint: this.serviceType.code === OfferServiceTypeEnum.FTL ? undefined : this.pickupAccessConstraint, //Si le transport est conditionné, on supprime les contraintes d'accès
      pickupLoadingConstraint: this.pickupLoadingConstraint,
      pickupRegisteredAddressId: this.pickupRegisteredAddressId,
      pickupTimeslotEnd: this.pickupTimeslotEnd,
      pickupTimeslotStart: this.pickupTimeslotStart,
      pickupMeeting: this.pickupMeeting,
      pickupFloor: this.pickupFloor,
      pickupComments: this.pickupComments,
      pickupPhoneCall: this.pickupPhoneCall,
      pickupDate: this.pickupDate && dayjs(this.pickupDate).isValid() ? dayjs(this.pickupDate).format(DATE_FORMAT) : undefined,
      pickupReference: this.pickupReference,
      totalPallets: this.totalPallets,
      totalParcel: this.totalParcel,
      totalWeight: this.totalWeight,
      transportExtraConditions: this.transportExtraConditions,
      transporter: { id: this.transporter?.id },
      creationDate: this.creationDate && dayjs(this.creationDate).isValid() ? dayjs(this.creationDate).toJSON() : undefined,
      creatorLogin: this.creatorLogin,
      creatorUuid: this.creatorUuid,
      offerId: this.offerId,
      offerName: this.offerName,
      quotationStatus: this.quotationStatus,
      quotationValidityDate: this.quotationValidityDate?.isValid() ? this.quotationValidityDate.toJSON() : undefined,
      quotationBasePrice: this.quotationBasePrice,
      quotationPriceHT: this.quotationPriceHT,
      quotationRejectionMessage: this.quotationRejectionMessage,
      itemsNature: this.itemsNature,
      goodsValue: this.goodsValue,
      euroPallet: this.euroPallet,
      billingQuantity: this.billingQuantity,
      totalVolume: this.totalVolume,
      totalLDM: this.totalLDM,
      distance: this.distance,
      outsideEuDeliveryCountry: this.outsideEuDeliveryCountry,
      outsideEuDeliveryCity: this.outsideEuDeliveryCity,
      outsideEuDeliveryComments: this.outsideEuDeliveryComments,
      orderTunnelStep: this.orderTunnelStep,
    };
  }

  public updateTotalsTransport(): void {
    let totalPallets = 0;
    let totalParcels = 0;
    let totalWeight = 0;

    this.items
      ?.map(it => it.serialize())
      .forEach(it => {
        if (it.weight) {
          totalWeight += it.weight; //it.weight => contient le poids total de l'item
        }
        if (it.quantity) {
          if (it.handlingType === HandlingTypeEnum.PALLET) {
            totalPallets += it.quantity;
          } else if (it.handlingType === HandlingTypeEnum.PARCEL) {
            totalParcels += it.quantity;
          }
        }
      });

    this.totalPallets = totalPallets;
    this.totalParcel = totalParcels;
    this.totalWeight = totalWeight;
  }

  public checkItems(): boolean {
    const items = this.items || [];
    if (this.merchandiseType.code === OfferMerchandiseTypeEnum.CONDITIONED) {
      return items.every((item: TransportItem) => item.quantity && item.length && item.width && item.maxHeight && item.weight && item.handlingType);
    }
    if (this.merchandiseType.code === OfferMerchandiseTypeEnum.UNCONDITIONED) {
      return items.every(
        (item: TransportItem) =>
          ((item.length && item.width && item.maxHeight) || item.volume) &&
          item.weight &&
          !item.handlingType &&
          ((item.vehicleCategory && item.vehicleType) || item.vehicleNotFound)
      );
    }
    return true;
  }

  public checkAddresses(): boolean {
    return !!this.pickupAddressZipCode && !!this.pickupAddressCountry && !!this.deliveryAddressZipCode && !!this.deliveryAddressCountry;
  }
}

export function normalizeEmptyString(value: string | undefined | null): string | undefined {
  return value && value.trim().length > 0 ? value : undefined;
}
